/**
 * IXVerify v2 — result screens (ixv2 WP-3.2; plan §3.2 step 5 + mismatch path).
 *
 * Success: full-bleed green — "Published · sent to CRIO + you", verification
 * code, share/view-PDF, request second check.
 * Mismatch: full-bleed red — the UI celebrates the catch ("IXVerify just
 * prevented a wrong-kit dispense"): a mismatch record is the tool WORKING.
 * Color never carries the meaning alone (icon + words on both).
 *
 * Pilot r3: the mismatch screen is also the "what happened" surface — it
 * shows the assigned-vs-scanned breakdown and the recorded reason, and offers
 * the audited reopen for the misread case (e.g. the extraction confirmed the
 * LOT number as the kit number, so every honest scan "mismatched"). The
 * record itself always stands; the reopen is its recorded follow-up.
 */
import { useState } from 'react';
import { CheckCircle2, FileCheck2, FileSearch, Home, Loader2, Pencil, Plus, Share2, ShieldAlert, UserCheck } from 'lucide-react';
import type { PublishResult, SessionDetail } from '../../lib/api';

const lightBtn =
  'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none';

export function SuccessScreen({
  result,
  onViewReport,
  onShare,
  canShare,
  onRequestSecondCheck,
  onNewDispense,
  onDone,
}: {
  result: PublishResult;
  onViewReport: () => void;
  onShare: () => void;
  canShare: boolean;
  onRequestSecondCheck: () => void;
  onNewDispense: () => void;
  onDone: () => void;
}) {
  const sentTo = result.distribution || [];
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto bg-green-600 px-6 text-center text-white"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 2rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)',
      }}
      data-testid="success-screen"
    >
      <CheckCircle2 className="h-16 w-16" aria-hidden="true" />
      <h1 className="mt-4 text-2xl font-bold">Verified &amp; published</h1>
      <p className="mt-1 text-lg font-semibold tabular-nums">{result.report.verification_code}</p>
      <p className="mt-2 max-w-xs text-sm text-white/90">
        {sentTo.length > 0
          ? `Report on its way to ${sentTo.length} recipient${sentTo.length === 1 ? '' : 's'}.`
          : 'Report sealed and stored.'}
      </p>

      <div className="mt-6 flex w-full max-w-xs flex-col gap-2">
        <button type="button" onClick={onViewReport} className={lightBtn}>
          <FileSearch className="h-4 w-4" aria-hidden="true" /> View report
        </button>
        {canShare && (
          <button type="button" onClick={onShare} className={lightBtn}>
            <Share2 className="h-4 w-4" aria-hidden="true" /> Share
          </button>
        )}
        <button type="button" onClick={onRequestSecondCheck} className={lightBtn} data-testid="request-second-check">
          <UserCheck className="h-4 w-4" aria-hidden="true" /> Request second check
        </button>
      </div>

      <div className="mt-8 flex w-full max-w-xs flex-col gap-2">
        <button
          type="button"
          onClick={onNewDispense}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-green-700 transition-colors duration-150 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none"
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> New dispense
        </button>
        <button type="button" onClick={onDone} className={lightBtn}>
          <Home className="h-4 w-4" aria-hidden="true" /> Done
        </button>
      </div>
    </div>
  );
}

export function MismatchRecordedScreen({
  detail,
  onDone,
  onReopen,
  reopening,
}: {
  detail: SessionDetail | null;
  onDone: () => void;
  onReopen: (reason: string) => void;
  reopening: boolean;
}) {
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const records = detail?.session.mismatch_records || [];
  const record = records.length > 0 ? records[records.length - 1] : null;
  const discrepancies = (detail?.kits || []).filter(
    (k) => !['matched', 'manual_confirmed'].includes(k.match_status)
  );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto bg-red-600 px-6 text-center text-white"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 2rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)',
      }}
      data-testid="mismatch-screen"
    >
      <div className="my-auto flex w-full max-w-sm flex-col items-center py-4">
        <ShieldAlert className="h-16 w-16" aria-hidden="true" />
        <h1 className="mt-4 text-2xl font-bold">Mismatch recorded</h1>
        <p className="mt-2 max-w-xs text-sm text-white/90">
          IXVerify just prevented a wrong-kit dispense. Site admins have been notified and the
          mismatch is on file &mdash; nothing dispenses from this record as it stands.
        </p>
        <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-white">
          <FileCheck2 className="h-4 w-4" aria-hidden="true" /> That's the tool working. Good catch.
        </p>

        {/* What happened — the read-back of the recorded state (pilot r3) */}
        {(discrepancies.length > 0 || record) && (
          <div className="mt-6 w-full rounded-xl bg-white/10 p-4 text-left" data-testid="mismatch-summary">
            {record?.recorded_at && (
              <p className="text-xs text-white/80">
                Recorded {new Date(record.recorded_at).toLocaleString()}
                {record.reason ? ` — ${record.reason}` : ''}
              </p>
            )}
            {discrepancies.length > 0 && (
              <ul className={`space-y-1 text-sm tabular-nums ${record?.recorded_at ? 'mt-2' : ''}`}>
                {discrepancies.map((k) => (
                  <li key={k.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      {k.assigned_kit_number || '(no assigned kit)'}
                      {k.scanned_kit_number && (
                        <span className="text-white/90"> &ne; {k.scanned_kit_number}</span>
                      )}
                    </span>
                    <span className="whitespace-nowrap text-xs uppercase tracking-wider text-white/70">
                      {k.match_status.replace('_', ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-8 w-full">
          <button
            type="button"
            onClick={onDone}
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none"
          >
            <Home className="h-4 w-4" aria-hidden="true" /> Back to dashboard
          </button>
        </div>

        {/* Recorded in error? The audited way back (pilot r3). Deliberately
            quiet — a real catch should end at "Back to dashboard". */}
        {!reopenOpen ? (
          <button
            type="button"
            onClick={() => { setReopenOpen(true); setReopenReason(''); }}
            className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-white/80 underline underline-offset-2 transition-colors duration-150 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none sm:min-h-0"
            data-testid="reopen-mismatch"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" /> Recorded in error? Reopen &amp; correct
          </button>
        ) : (
          <div className="mt-4 w-full rounded-xl bg-white p-4 text-left" data-testid="reopen-sheet">
            <h2 className="text-sm font-semibold text-gray-900">Reopen this mismatch</h2>
            <p className="mt-1 text-xs text-gray-500">
              The mismatch record stays on file, the reopen is recorded in the audit trail, site
              admins are notified, and any report published later discloses both. Say what was
              wrong &mdash; e.g. the assignment was misread and the lot number was used as the kit number.
            </p>
            <textarea
              aria-label="Reason for reopening"
              className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none"
              rows={2}
              placeholder="Why is this being reopened?"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReopenOpen(false)}
                disabled={reopening}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 motion-reduce:transition-none sm:min-h-0"
              >
                Keep the record
              </button>
              <button
                type="button"
                disabled={reopening || !reopenReason.trim()}
                onClick={() => onReopen(reopenReason.trim())}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none sm:min-h-0"
                data-testid="reopen-confirm"
              >
                {reopening && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                Reopen &amp; correct
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
